<template>
  <!-- Job documents dropdown: lists every non-image media file attached to a
       job plus the rendered Partner Offer PDF. Shared between the jobs list
       row (actions column) and the open-job tab header so both surfaces expose
       the exact same set of downloadable documents. -->
  <b-dropdown
    right
    size="sm"
    text="Docs"
    boundary="viewport"
    menu-class="job-docs-scroll-menu"
  >
    <template
      v-for="(path, index) in nonImagePaths(files, 'meter_application')"
    >
      <b-dropdown-item
        :key="`meter-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.meter_application', { index }) }}
      </b-dropdown-item>
    </template>
    <template v-for="(path, index) in nonImagePaths(files, 'picture_before')">
      <b-dropdown-item
        :key="`pic-before-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.start') }}
      </b-dropdown-item>
    </template>
    <template v-for="(path, index) in nonImagePaths(files, 'picture_after')">
      <b-dropdown-item
        :key="`pic-after-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.finish') }}
      </b-dropdown-item>
    </template>
    <template v-for="(path, index) in nonImagePaths(files, 'protocol')">
      <b-dropdown-item
        :key="`protocol-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.protocol') }}
      </b-dropdown-item>
    </template>
    <template
      v-for="(path, index) in nonImagePaths(files, 'customer_protocol')"
    >
      <b-dropdown-item
        :key="`customer-protocol-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.customer_protocol') }}
      </b-dropdown-item>
    </template>
    <template
      v-for="(path, index) in nonImagePaths(files, 'customer_signoff_feedback')"
    >
      <b-dropdown-item
        :key="`signoff-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.customer_signoff_feedback', { index }) }}
      </b-dropdown-item>
    </template>
    <template
      v-for="(path, index) in nonImagePaths(files, 'customer_blueprint')"
    >
      <b-dropdown-item
        :key="`blueprint-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.customer_blueprint', { index }) }}
      </b-dropdown-item>
    </template>
    <template
      v-for="(path, index) in nonImagePaths(files, 'customer_documentation')"
    >
      <b-dropdown-item
        :key="`documentation-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.customer_documentation', { index }) }}
      </b-dropdown-item>
    </template>
    <template
      v-for="(path, index) in nonImagePaths(files, 'propagated_protocol')"
    >
      <b-dropdown-item
        :key="`propagated-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.propagated_protocol', { index }) }}
      </b-dropdown-item>
    </template>
    <template v-for="(path, index) in nonImagePaths(files, 'picture')">
      <b-dropdown-item
        :key="`picture-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.picture', { index }) }}
      </b-dropdown-item>
    </template>
    <template
      v-for="(path, index) in nonImagePaths(files, 'planning_customer')"
    >
      <b-dropdown-item
        :key="`planning-customer-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.planning_customer', { index }) }}
      </b-dropdown-item>
    </template>
    <template v-for="(path, index) in nonImagePaths(files, 'planning_partner')">
      <b-dropdown-item
        :key="`planning-partner-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.planning_partner', { index }) }}
      </b-dropdown-item>
    </template>
    <template
      v-for="(path, index) in nonImagePaths(files, 'cancellation_protocol')"
    >
      <b-dropdown-item
        :key="`cancellation-protocol-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.cancellation_protocol', { index }) }}
      </b-dropdown-item>
    </template>
    <template
      v-for="(path, index) in nonImagePaths(files, 'cancellation_picture')"
    >
      <b-dropdown-item
        :key="`cancellation-picture-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.cancellation_picture', { index }) }}
      </b-dropdown-item>
    </template>
    <template
      v-for="(path, index) in nonImagePaths(
        files,
        'cancellation_picture_before',
      )"
    >
      <b-dropdown-item
        :key="`cancellation-picture-before-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.cancellation_picture_before') }}
      </b-dropdown-item>
    </template>
    <template
      v-for="(path, index) in nonImagePaths(
        files,
        'cancellation_picture_after',
      )"
    >
      <b-dropdown-item
        :key="`cancellation-picture-after-${index}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ $t('job.image.cancellation_picture_after') }}
      </b-dropdown-item>
    </template>
    <b-dropdown-item
      v-if="jobName"
      :href="
        `/documents-api/pdf/accounting/documents/render-offer/${jobName}?jobId=${jobSfid}`
          | jwtToken
      "
      target="_blank"
    >
      Partner Offer PDF
    </b-dropdown-item>
    <template v-for="fileGroup in getExtraJobMediaFiles(files)">
      <b-dropdown-item
        v-for="(path, j) in nonImagePathsList(fileGroup.paths)"
        :key="`${fileGroup.key}-${j}`"
        :href="`/api/media/file/${path}?jwt=${access_token}`"
        target="_blank"
      >
        {{ fileGroup.label }} [{{ j }}]
      </b-dropdown-item>
    </template>
  </b-dropdown>
</template>

<script>
import { mapGetters } from 'vuex';
import { JobMediaFileTypeEnum } from '@gid/models';

export default {
  name: 'JobDocsDropdown',
  props: {
    files: {
      type: Object,
      default: () => ({}),
    },
    jobName: {
      type: String,
      default: '',
    },
    jobSfid: {
      type: String,
      default: '',
    },
    // Job-media-file-type picklist, loaded once by the parent so extra
    // (non-enum) media groups get human-readable labels instead of raw keys.
    jmfPicklist: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters(['access_token']),
  },
  methods: {
    isImagePath(path) {
      return /\.(jpe?g|png|webp|gif|heic|heif|bmp|svg)$/i.test(path || '');
    },
    nonImagePathsList(paths) {
      if (!Array.isArray(paths)) return [];
      return paths.filter((p) => !this.isImagePath(p));
    },
    nonImagePaths(files, key) {
      const paths = files?.[key]?.paths;
      return this.nonImagePathsList(paths);
    },
    getExtraJobMediaFiles(files) {
      if (!files) {
        return [];
      }

      const filterArray = Object.values(JobMediaFileTypeEnum);

      return Object.keys(files)
        .filter((key) => !filterArray.includes(key))
        .map((key) => {
          const filteredPicklistValue = this.jmfPicklist.find(
            (item) => item.valueName === key,
          );

          return {
            key,
            ...files[key],
            label: filteredPicklistValue?.label || key.replace(/_/g, ' '),
          };
        });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .job-docs-scroll-menu {
  max-height: 300px !important; /* Limits the height */
  overflow-y: auto !important; /* Enables the scrollbar */
  overflow-x: hidden;
  border: 1px solid #dee2e6;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

::v-deep .job-docs-scroll-menu::-webkit-scrollbar {
  width: 5px;
}
::v-deep .job-docs-scroll-menu::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}
</style>
